// Shared formatter for "N minutes late" copy used by the dashboard
// alerts strip, the attendance session page, the student row and any
// other place a lateness duration is rendered in plain English.
//
// Aug 2026 redesign: formatters now emit compact "hr / min" units
// per operational dashboard spec (e.g. 541 → "9 hr 1 min"). The
// dashboard appends the word "late" itself so both a naked count
// and a full "9 hr 1 min late" caption can share the same helper.

export function toWholeLateMinutes(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

/**
 * Compact "hr min" formatter — NO trailing " late" so callers can
 * append their own suffix (or use the row/badge affordance). Examples:
 *   5     → "5 min"
 *   60    → "1 hr"
 *   70    → "1 hr 10 min"
 *   120   → "2 hr"
 *   541   → "9 hr 1 min"
 */
export function formatLateDurationCompact(value: number | string | null | undefined): string {
  const mins = toWholeLateMinutes(value);
  if (mins < 1) return "under 1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/**
 * Compact "hr min late" variant for locations that want the full
 * caption. Uses the same formatter to guarantee consistency across
 * every surface.
 */
export function formatLateDuration(value: number | string | null | undefined): string {
  const base = formatLateDurationCompact(value);
  if (base === "under 1 min") return "less than a minute late";
  return `${base} late`;
}
